import { parseCSV } from '../utils/csv'
import {
    istDateTime,
    istDateTimeEpoch,
    istDateTimeInput,
    nowUnix,
} from '../utils/datetime'

/**
 * Trainer dashboard notifications: the form's rules and the API's shapes
 * (CommunicationManagementService /comms/trainer-notifications, contract v2).
 * All times are epoch seconds; the form reads and shows them in IST, as the
 * rest of this app does.
 */

export const MAX_TITLE = 80
export const MAX_MESSAGE = 120

export const DRAFT = 'DRAFT'
export const PUBLISHED = 'PUBLISHED'

export const LIFECYCLE = {
    DRAFT: 'DRAFT',
    SCHEDULED: 'SCHEDULED',
    ACTIVE: 'ACTIVE',
    EXPIRED: 'EXPIRED',
}

export const LIFECYCLE_LABEL = {
    DRAFT: 'Draft',
    SCHEDULED: 'Scheduled',
    ACTIVE: 'Active',
    EXPIRED: 'Expired',
}

// The end date the first version stored for "no expiry" (2100-01-01).
export const NO_EXPIRY = 4102444800

// TRAINING sits on the strip at priority 4, ANNOUNCEMENT at 5.
export const CATEGORIES = [
    { value: 'TRAINING', label: 'TGP Training' },
    { value: 'ANNOUNCEMENT', label: 'Announcement / Notification' },
]

export const CATEGORY_LABEL = {
    ANNOUNCEMENT: 'Announcement',
    TRAINING: 'TGP Training',
}

/** The tag pill's "visual treatment"; `tone` picks its TAG token colours. */
export const TAGS = [
    { value: 'URGENT', label: 'Urgent', tone: 'error' },
    { value: 'UPDATE', label: 'Update', tone: 'pinpoint' },
    { value: 'IMPORTANT', label: 'Important', tone: 'warning' },
    { value: 'MANDATORY', label: 'Mandatory', tone: 'success' },
    { value: 'SOS', label: 'SOS', tone: 'error' },
]

export const tagLabel = (value) =>
    TAGS.find((t) => t.value === value)?.label ?? value ?? ''

export const CTA_INTERNAL = 'internal'
export const CTA_EXTERNAL = 'external'

// trainer-ui pages an internal CTA can open (applause PR #719's list).
export const INTERNAL_ROUTES = [
    { value: '/', label: 'Home (/)' },
    { value: '/trainer-connect', label: 'Trainer Connect' },
    { value: '/session-feedback', label: 'Session Feedback' },
    { value: '/payouts', label: 'Payouts' },
    { value: '/calendar-new', label: 'Calendar' },
    { value: '/student-report', label: 'Student Report' },
    { value: '/batch-tracker', label: 'Batch Tracker' },
    { value: '/trainer-profile', label: 'Trainer Profile' },
    { value: '/logbooks', label: 'Logbooks' },
    { value: '/sharpen-up', label: 'Sharpen Up' },
]

export const AUDIENCE = {
    ALL: 'ALL',
    COHORT: 'COHORT',
    TRAINER_IDS: 'TRAINER_IDS',
}

// How the audience is chosen: the quick segments or a list of trainers.
export const SEGMENTS_MODE = 'segments'
export const CSV_MODE = 'csv'

const DEFAULT_ACK_LABEL = 'Acknowledge'

/** A new notification: start now, acknowledgement on, no audience yet. */
export const emptyTrainerNotificationForm = (now = nowUnix()) => ({
    title: '',
    category: 'ANNOUNCEMENT',
    tag: '',
    message: '',
    ctaLabel: '',
    ctaType: CTA_INTERNAL,
    ctaLink: '',
    ackRequired: true,
    ackLabel: DEFAULT_ACK_LABEL,
    start: istDateTimeInput(now),
    end: '',
    audienceMode: SEGMENTS_MODE,
    allTrainers: false,
    // Which quick segments are ticked; an unticked one sends no values.
    segments: { grade: false, region: false, product: false },
    gradeGroups: [],
    regions: [],
    productIds: [],
    trainerIds: [],
})

/** The form for a saved notification (editing a draft). */
export const formFromNotification = (n) => {
    const base = emptyTrainerNotificationForm()
    const type = n?.audience_type
    const gradeGroups = n?.audience_grade_groups ?? []
    const regions = n?.audience_regions ?? []
    const productIds = n?.audience_product_ids ?? []
    return {
        ...base,
        title: n?.title ?? '',
        category: n?.category || base.category,
        tag: n?.tag ?? '',
        message: n?.message ?? '',
        ctaLabel: n?.cta_label ?? '',
        ctaType: n?.cta_type === CTA_EXTERNAL ? CTA_EXTERNAL : CTA_INTERNAL,
        ctaLink: n?.cta_link ?? '',
        ackRequired: n?.ack_required ?? true,
        ackLabel: n?.ack_label || DEFAULT_ACK_LABEL,
        start: n?.start_date ? istDateTimeInput(n.start_date) : base.start,
        // Empty = no expiry (also how the far-future sentinel is stored).
        end:
            n?.end_date && Number(n.end_date) < NO_EXPIRY
                ? istDateTimeInput(n.end_date)
                : '',
        audienceMode: type === AUDIENCE.TRAINER_IDS ? CSV_MODE : SEGMENTS_MODE,
        allTrainers: type === AUDIENCE.ALL,
        segments: {
            grade: type === AUDIENCE.COHORT && gradeGroups.length > 0,
            region: type === AUDIENCE.COHORT && regions.length > 0,
            product: type === AUDIENCE.COHORT && productIds.length > 0,
        },
        gradeGroups,
        regions,
        productIds,
        trainerIds: (n?.audience_trainer_ids ?? []).map(String),
    }
}

/** The audience the form describes: ALL, COHORT, TRAINER_IDS, or null for none yet. */
export const audienceTypeOf = (form) => {
    if (form.audienceMode === CSV_MODE) return AUDIENCE.TRAINER_IDS
    if (form.allTrainers) return AUDIENCE.ALL
    const { grade, region, product } = form.segments
    return grade || region || product ? AUDIENCE.COHORT : null
}

/** The cohort filters actually sent: only the ticked segments' values. */
const cohortFilters = (form) => ({
    gradeGroups: form.segments.grade ? form.gradeGroups : [],
    regions: form.segments.region ? form.regions : [],
    productIds: form.segments.product ? form.productIds : [],
})

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const DIGITS_RE = /^\d+$/

/** A trainer id the API accepts: a trainer's email or numeric id. */
export const isTrainerId = (value) =>
    EMAIL_RE.test(value) || DIGITS_RE.test(value)

// Header names a trainer column may have, most specific first.
const ID_HEADERS = ['trainer_id', 'teacher_id', 'email']

const unique = (list) => [...new Set(list)]

/**
 * Trainer ids from a CSV file's text: the `trainer_id`, `teacher_id` or
 * `email` column, or the only column of a one-column file (whose first line
 * is an id too when it is not a header).
 *
 * @param {string} text
 * @returns {{ids: string[], invalid: string[], error: string}} `invalid`
 *     holds the values that are neither an email nor a number
 */
export const trainerIdsFromCsv = (text) => {
    const { headers, rows } = parseCSV(text)
    if (!headers.length)
        return { ids: [], invalid: [], error: 'The file is empty.' }
    const lower = headers.map((h) => h.toLowerCase())
    const named = ID_HEADERS.map((h) => lower.indexOf(h)).find((i) => i >= 0)
    let values
    if (named !== undefined) {
        values = rows.map((r) => r[headers[named]])
    } else if (headers.length === 1) {
        values = [headers[0], ...rows.map((r) => r[headers[0]])]
    } else {
        return {
            ids: [],
            invalid: [],
            error: 'The file needs a trainer_id, teacher_id or email column, or a single column of ids.',
        }
    }
    return splitValid(values)
}

/** Trainer ids typed or pasted: separated by commas, spaces or new lines. */
export const trainerIdsFromText = (text) =>
    splitValid(String(text ?? '').split(/[\s,;]+/))

const splitValid = (values) => {
    const cleaned = unique(
        values.map((v) => String(v ?? '').trim()).filter(Boolean)
    )
    return {
        ids: cleaned.filter(isTrainerId),
        invalid: cleaned.filter((v) => !isTrainerId(v)),
        error: '',
    }
}

/**
 * The body POST /tms/v2/trainer-notifications/audience-count expects: the
 * audience expanded through the audience options (grade groups to their
 * grades, products to their batch types). Null when there is no audience to
 * count yet.
 *
 * @param {object} form
 * @param {{grade_groups: object[], products: object[]}} options  A1's answer
 */
export const audienceCountBody = (form, options) => {
    const type = audienceTypeOf(form)
    if (!type) return null
    const body = {
        audience_type: type,
        audience_grades: [],
        audience_regions: [],
        audience_batch_types: [],
        audience_trainer_ids: [],
    }
    if (type === AUDIENCE.TRAINER_IDS) {
        if (!form.trainerIds.length) return null
        return { ...body, audience_trainer_ids: form.trainerIds }
    }
    if (type === AUDIENCE.ALL) return body
    const { gradeGroups, regions, productIds } = cohortFilters(form)
    if (!gradeGroups.length && !regions.length && !productIds.length)
        return null
    const groups = options?.grade_groups ?? []
    const products = options?.products ?? []
    return {
        ...body,
        audience_grades: unique(
            gradeGroups.flatMap(
                (g) => groups.find((o) => o.value === g)?.grades ?? []
            )
        ),
        audience_regions: regions,
        audience_batch_types: unique(
            productIds
                .map(
                    (id) =>
                        products.find((p) => p.product_id === id)?.batch_type
                )
                .filter(Boolean)
        ),
    }
}

const epochOf = (value) => istDateTimeEpoch(value)

/**
 * The create / edit body for `status` (DRAFT or PUBLISHED). A draft leaves
 * out what is still empty, so the API stores only what was filled.
 */
export const trainerNotificationPayload = (form, status) => {
    const type = audienceTypeOf(form)
    const { gradeGroups, regions, productIds } = cohortFilters(form)
    const body = {
        status,
        title: form.title.trim(),
        message: form.message.trim(),
        category: form.category,
        tag: form.tag,
        cta_label: form.ctaLabel.trim(),
        cta_link: form.ctaLink.trim(),
        cta_type: form.ctaType,
        ack_required: form.ackRequired,
        ack_label: form.ackRequired ? form.ackLabel.trim() : '',
        start_date: epochOf(form.start),
        end_date: epochOf(form.end),
        audience_type: type,
        audience_grade_groups: type === AUDIENCE.COHORT ? gradeGroups : [],
        audience_regions: type === AUDIENCE.COHORT ? regions : [],
        audience_product_ids: type === AUDIENCE.COHORT ? productIds : [],
        audience_trainer_ids:
            type === AUDIENCE.TRAINER_IDS ? form.trainerIds : [],
    }
    if (status === PUBLISHED) return body
    return Object.fromEntries(
        Object.entries(body).filter(
            ([, v]) => v !== '' && v !== null && v !== undefined
        )
    )
}

/**
 * What is wrong with the form for `status`, keyed by field; empty when it
 * can be saved. A draft needs only its name; publishing needs everything the
 * API checks (contract A2).
 *
 * @param {object} form
 * @param {string} status  DRAFT or PUBLISHED
 * @param {number} now     epoch seconds
 * @returns {Object<string, string>}
 */
export const validateTrainerNotification = (form, status, now = nowUnix()) => {
    const errors = {}
    const title = form.title.trim()
    if (!title) errors.title = 'Enter a notification name'
    else if (title.length > MAX_TITLE)
        errors.title = `At most ${MAX_TITLE} characters`
    if (status !== PUBLISHED) return errors

    const message = form.message.trim()
    if (!message) errors.message = 'Enter the message'
    else if (message.length > MAX_MESSAGE)
        errors.message = `At most ${MAX_MESSAGE} characters`
    if (!form.tag) errors.tag = 'Choose a tag'

    const label = form.ctaLabel.trim()
    const link = form.ctaLink.trim()
    if (!label) errors.ctaLabel = 'Enter the button label'
    if (!link)
        errors.ctaLink =
            form.ctaType === CTA_INTERNAL
                ? 'Choose the page it opens'
                : 'Enter the link'
    else if (form.ctaType === CTA_EXTERNAL && !isHttpsUrl(link))
        errors.ctaLink = 'Enter a full https:// link'
    else if (form.ctaType === CTA_INTERNAL && !link.startsWith('/'))
        errors.ctaLink = 'A dashboard page starts with /'
    if (form.ackRequired && !form.ackLabel.trim())
        errors.ackLabel = 'Enter the acknowledge button text'

    const start = epochOf(form.start)
    const end = epochOf(form.end)
    if (!start) errors.start = 'Enter the start date and time'
    // Expiry is optional: empty means it stays up until deactivated. A start
    // already past is published as "now" (contract A2).
    if (form.end && !end) errors.end = 'Enter a valid end date and time'
    else if (end && start && end <= Math.max(start, now))
        errors.end = 'Must be after the start and in the future'

    const type = audienceTypeOf(form)
    if (!type) errors.audience = 'Choose who receives this'
    else if (type === AUDIENCE.TRAINER_IDS && !form.trainerIds.length)
        errors.audience = 'Add at least one trainer'
    else if (type === AUDIENCE.COHORT) {
        const { grade, region, product } = form.segments
        if (grade && !form.gradeGroups.length)
            errors.gradeGroups = 'Choose at least one grade group'
        if (region && !form.regions.length)
            errors.regions = 'Choose at least one region'
        if (product && !form.productIds.length)
            errors.productIds = 'Choose at least one product'
    }
    return errors
}

/** True for an absolute https:// URL with a host. */
export const isHttpsUrl = (value) => {
    try {
        const url = new URL(value)
        return url.protocol === 'https:' && !!url.hostname
    } catch {
        return false
    }
}

const labelFrom = (list, value, key = 'value', name = 'label') =>
    list?.find((o) => o[key] === value)?.[name] ?? value

/**
 * The audience in words, one part per filter: ["All trainers"],
 * ["Grade group: UKG-1, 2-3", "Region: India"] or ["12 trainers"].
 * Takes the API's notification fields; `options` (A1) turns region codes and
 * product ids into names, and is optional.
 */
export const audienceSummary = (n, options) => {
    const type = n?.audience_type
    if (type === AUDIENCE.ALL) return ['All trainers']
    if (type === AUDIENCE.TRAINER_IDS) {
        const count = n.audience_trainer_ids?.length ?? 0
        return [`${count} trainer${count === 1 ? '' : 's'}`]
    }
    if (type !== AUDIENCE.COHORT) return []
    const parts = []
    if (n.audience_grade_groups?.length)
        parts.push(`Grade group: ${n.audience_grade_groups.join(', ')}`)
    if (n.audience_regions?.length)
        parts.push(
            `Region: ${n.audience_regions
                .map((r) => labelFrom(options?.regions, r))
                .join(', ')}`
        )
    if (n.audience_product_ids?.length)
        parts.push(
            `Product: ${n.audience_product_ids
                .map((p) =>
                    labelFrom(
                        options?.products,
                        p,
                        'product_id',
                        options?.products?.[0]?.label ? 'label' : 'name'
                    )
                )
                .join(', ')}`
        )
    return parts
}

/** The form's audience in the API's field names, for audienceSummary. */
export const formAudience = (form) => {
    const type = audienceTypeOf(form)
    const { gradeGroups, regions, productIds } = cohortFilters(form)
    return {
        audience_type: type,
        audience_grade_groups: gradeGroups,
        audience_regions: regions,
        audience_product_ids: productIds,
        audience_trainer_ids: form.trainerIds,
    }
}

/** A start or end for display: IST date and time, "No expiry" for the old far-future end. */
export const whenText = (epoch) => {
    if (!epoch) return '-'
    if (Number(epoch) >= NO_EXPIRY) return 'No expiry'
    return `${istDateTime(epoch)} IST`
}
